/**
 * ChecklistComponent
 * REFACTORED: Extracted SortableChecklistItem component and useChecklistOperations hook
 */

import React, { useState, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { ChecklistItem } from '@/shared/types/projectData';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { cn, useTheme } from '@/ui/theme/ThemeContext';
import { useKeyboardShortcut } from '@/shared/services/KeyboardEventManager';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

// Import extracted components and hooks
import { SortableChecklistItem } from './SortableChecklistItem';
import { useChecklistOperations } from './hooks/useChecklistOperations';

interface ChecklistComponentProps {
  checklist: ChecklistItem[];
  onChange: (updatedChecklist: ChecklistItem[]) => void;
  className?: string;
}

export const ChecklistComponent: React.FC<ChecklistComponentProps> = ({
  checklist,
  onChange,
  className,
}) => {
  const { theme } = useTheme();
  const { t } = useSettings();
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const newItemInputRef = useRef<HTMLInputElement>(null);
  const sortedChecklist = React.useMemo(
    () => [...checklist].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [checklist],
  );

  // Use extracted operations hook
  const {
    newItemText,
    setNewItemText,
    isAddingItem,
    setIsAddingItem,
    handleDragEnd,
    handleToggleItem,
    handleUpdateItem,
    handleDeleteItem,
    handleAddItem: _handleAddItem,
    handleAddItemKeyDown,
  } = useChecklistOperations({ checklist: sortedChecklist, onChange });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (isAddingItem && newItemInputRef.current) {
      newItemInputRef.current.focus();
    }
  }, [isAddingItem]);

  // Ctrl/Cmd+I opens the composer. Going through the keyboard manager without
  // allowInInput leaves the shortcut (and the browser default) to whichever
  // editable field currently has focus.
  const openComposer = React.useCallback(() => setIsAddingItem(true), [setIsAddingItem]);
  const composerShortcutEnabled = !editingItemId && !isAddingItem;
  useKeyboardShortcut('i', openComposer, {
    modifiers: { ctrl: true },
    priority: 100,
    preventDefault: true,
    allowInInput: false,
    enabled: composerShortcutEnabled,
    // i18n-raw-ui-literal-exempt: Keyboard-manager registration metadata, not rendered product UI.
    description: 'Add checklist item (Ctrl+I)',
  });
  useKeyboardShortcut('i', openComposer, {
    modifiers: { cmd: true },
    priority: 100,
    preventDefault: true,
    allowInInput: false,
    enabled: composerShortcutEnabled,
    // i18n-raw-ui-literal-exempt: Keyboard-manager registration metadata, not rendered product UI.
    description: 'Add checklist item (Cmd+I)',
  });

  const completedCount = checklist.filter(item => item.checked).length;
  const totalCount = checklist.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className={cn('space-y-3', className)} role="group" aria-labelledby="checklist-heading">
      {/* Header with Progress */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 id="checklist-heading" className={cn('text-sm font-medium', theme.textPrimary)}>
            {t('Checklist')}
          </h3>
          {totalCount > 0 && (
            <div className="flex items-center gap-2">
              <div className={cn('text-xs', theme.textSecondary)}>
                {completedCount}/{totalCount}
              </div>
              <div className="w-16 h-1.5 bg-slate-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          )}
        </div>
        
        <button
          onClick={() => setIsAddingItem(true)}
          className={cn(
            'flex items-center gap-1 px-2 py-1 text-xs rounded transition-all',
            theme.hover,
            theme.textSecondary
          )}
          title={t('Add checklist item (Ctrl/Cmd + I)')}
          aria-label={t('Add new checklist item')}
        >
          <Plus className="w-3 h-3" />
          {t('Add item')}
        </button>
      </div>

      {/* Checklist Items */}
      {totalCount > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortedChecklist.map(item => item.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-1">
              {sortedChecklist.map((item) => (
                  <SortableChecklistItem
                    key={item.id}
                    item={item}
                    onToggle={handleToggleItem}
                    onUpdate={handleUpdateItem}
                    onDelete={handleDeleteItem}
                    isEditing={editingItemId === item.id}
                    onStartEdit={() => setEditingItemId(item.id)}
                    onFinishEdit={() => setEditingItemId(null)}
                  />
                ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add New Item Input */}
      {isAddingItem && (
        <div className={cn('flex items-center gap-2 p-2 rounded-lg', theme.bgSecondary)}>
          <div className="w-5 h-5 rounded border-2 border-gray-300 dark:border-gray-600 flex-shrink-0" />
          <input
            ref={newItemInputRef}
            type="text"
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={handleAddItemKeyDown}
            onBlur={() => {
              if (!newItemText.trim()) {
                setIsAddingItem(false);
              }
            }}
            placeholder={t('Add a checklist item...')}
            className={cn(
              'flex-1 px-2 py-1 text-sm bg-transparent border rounded',
              theme.borderPrimary,
              theme.textPrimary,
              theme.focusRing
            )}
          />
        </div>
      )}

      {/* Empty State */}
      {totalCount === 0 && !isAddingItem && (
        <div 
          onClick={() => setIsAddingItem(true)}
          className={cn(
            'text-center py-8 rounded-lg border-2 border-dashed cursor-pointer transition-all',
            'border-gray-300 dark:border-gray-600',
            theme.hover,
            theme.textSecondary
          )}
        >
          <div className="flex flex-col items-center gap-2">
            <Plus className="w-5 h-5" />
            <span className="text-sm">{t('Add your first checklist item')}</span>
          </div>
        </div>
      )}
    </div>
  );
};
